import React, { useState, useEffect } from 'react';
import { Plus, Edit } from 'lucide-react';
import api from '../services/api';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import toast from 'react-hot-toast';
import './UserManagement.css';

export default function UserManagement() {
  const [activeTab, setActiveTab] = useState('universities');
  const [universities, setUniversities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableUniversities, setAvailableUniversities] = useState([]);
  const [universityForm, setUniversityForm] = useState({
    name: '',
    code: '',
    location: '',
    contact_email: '',
    phone: '',
  });
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'university_staff',
    university_id: '',
    phone: '',
  });

  useEffect(() => {
    if (activeTab === 'universities') {
      fetchUniversities();
    } else {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUniversities = async () => {
    try {
      setLoading(true);
      const data = await api.universities.list();
      setUniversities(Array.isArray(data) ? data : (data.universities || data.data || []));
    } catch (error) {
      toast.error('Failed to load universities');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const [usersData, unisData] = await Promise.all([
        api.users.list(),
        api.universities.list(),
      ]);
      setUsers(Array.isArray(usersData) ? usersData : (usersData.users || usersData.data || []));
      setAvailableUniversities(Array.isArray(unisData) ? unisData : (unisData.universities || unisData.data || []));
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUniversityModal = (university = null) => {
    if (university) {
      setSelectedUniversity(university);
      setUniversityForm({
        name: university.name,
        code: university.code,
        location: university.location,
        contact_email: university.contact_email,
        phone: university.phone,
      });
    } else {
      setSelectedUniversity(null);
      setUniversityForm({ name: '', code: '', location: '', contact_email: '', phone: '' });
    }
    setShowUniversityModal(true);
  };

  const handleOpenUserModal = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setUserForm({
        name: user.name,
        email: user.email,
        role: user.role,
        university_id: user.university_id || '',
        phone: user.phone,
      });
    } else {
      setSelectedUser(null);
      setUserForm({ name: '', email: '', role: 'university_staff', university_id: '', phone: '' });
    }
    setShowUserModal(true);
  };

  const handleSaveUniversity = async () => {
    if (!universityForm.name || !universityForm.code) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      if (selectedUniversity) {
        await api.universities.update(selectedUniversity.id, universityForm);
        toast.success('University updated successfully');
      } else {
        await api.universities.create(universityForm);
        toast.success('University created successfully');
      }
      setShowUniversityModal(false);
      await fetchUniversities();
    } catch (error) {
      toast.error('Failed to save university');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (userForm.role === 'university_staff' && !userForm.university_id) {
      toast.error('Please select a university for university staff');
      return;
    }

    try {
      setLoading(true);
      if (selectedUser) {
        await api.users.update(selectedUser.id, userForm);
        toast.success('User updated successfully');
      } else {
        await api.users.create(userForm);
        toast.success('User created successfully');
      }
      setShowUserModal(false);
      await fetchUsers();
    } catch (error) {
      toast.error('Failed to save user');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUniversityActive = async (universityId) => {
    try {
      await api.universities.toggleActive(universityId);
      await fetchUniversities();
      toast.success('University status updated');
    } catch (error) {
      toast.error('Failed to update university status');
      console.error(error);
    }
  };

  const handleToggleUserActive = async (userId) => {
    try {
      await api.users.toggleActive(userId);
      await fetchUsers();
      toast.success('User status updated');
    } catch (error) {
      toast.error('Failed to update user status');
      console.error(error);
    }
  };

  const handleResetPassword = async (userId) => {
    if (window.confirm('Reset password for this user?')) {
      try {
        await api.users.resetPassword(userId);
        toast.success('Password reset link sent');
      } catch (error) {
        toast.error('Failed to reset password');
        console.error(error);
      }
    }
  };

  const universityColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'code', label: 'Code', sortable: true },
    { key: 'location', label: 'Location', sortable: true },
    { key: 'contact_email', label: 'Contact Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={value ? 'status-badge status-completed' : 'status-badge status-closed'}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Actions',
      render: (value, row) => (
        <div className="action-buttons">
          <button className="btn btn-icon" onClick={() => handleOpenUniversityModal(row)}>
            <Edit size={16} />
          </button>
          <button className="btn btn-icon" onClick={() => handleToggleUniversityActive(value)}>
            {row.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
    },
  ];

  const userColumns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'role',
      label: 'Role',
      render: (value) => (
        <span className="status-badge status-new">
          {value?.replace(/_/g, ' ').toUpperCase()}
        </span>
      ),
    },
    { key: 'university_name', label: 'University', render: (value) => value || '-' },
    { key: 'phone', label: 'Phone', sortable: true },
    { key: 'default_password', label: 'Password', render: (value) => value || '-' },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={value ? 'status-badge status-completed' : 'status-badge status-closed'}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'id',
      label: 'Actions',
      render: (value, row) => (
        <div className="action-buttons">
          <button className="btn btn-icon" onClick={() => handleOpenUserModal(row)}>
            <Edit size={16} />
          </button>
          <button className="btn btn-icon" onClick={() => handleResetPassword(value)}>
            Reset
          </button>
          <button className="btn btn-icon" onClick={() => handleToggleUserActive(value)}>
            {row.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="management-page">
      <div className="management-header">
        <h1>Management</h1>
        <p className="text-gray">Manage universities and users</p>
      </div>

      <div className="management-tabs">
        <button
          className={`tab ${activeTab === 'universities' ? 'active' : ''}`}
          onClick={() => setActiveTab('universities')}
        >
          Universities
        </button>
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
      </div>

      <div className="management-container">
        <div className="section-header">
          <h2>{activeTab === 'universities' ? 'Universities' : 'Users'}</h2>
          <button
            className="btn btn-primary"
            onClick={() =>
              activeTab === 'universities'
                ? handleOpenUniversityModal()
                : handleOpenUserModal()
            }
          >
            <Plus size={18} />
            Add {activeTab === 'universities' ? 'University' : 'User'}
          </button>
        </div>

        {activeTab === 'universities' && (
          <DataTable
            columns={universityColumns}
            data={universities}
            loading={loading}
            emptyMessage="No universities found"
          />
        )}

        {activeTab === 'users' && (
          <DataTable
            columns={userColumns}
            data={users}
            loading={loading}
            emptyMessage="No users found"
          />
        )}
      </div>

      <Modal
        isOpen={showUniversityModal}
        onClose={() => setShowUniversityModal(false)}
        title={selectedUniversity ? 'Edit University' : 'Add University'}
      >
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input
            type="text"
            value={universityForm.name}
            onChange={(e) => setUniversityForm((prev) => ({ ...prev, name: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Code *</label>
          <input
            type="text"
            value={universityForm.code}
            onChange={(e) => setUniversityForm((prev) => ({ ...prev, code: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Location</label>
          <input
            type="text"
            value={universityForm.location}
            onChange={(e) => setUniversityForm((prev) => ({ ...prev, location: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Contact Email</label>
          <input
            type="email"
            value={universityForm.contact_email}
            onChange={(e) => setUniversityForm((prev) => ({ ...prev, contact_email: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input
            type="tel"
            value={universityForm.phone}
            onChange={(e) => setUniversityForm((prev) => ({ ...prev, phone: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowUniversityModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveUniversity} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={selectedUser ? 'Edit User' : 'Add User'}
      >
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input
            type="text"
            value={userForm.name}
            onChange={(e) => setUserForm((prev) => ({ ...prev, name: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input
            type="email"
            value={userForm.email}
            onChange={(e) => setUserForm((prev) => ({ ...prev, email: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <select
            value={userForm.role}
            onChange={(e) => setUserForm((prev) => ({ ...prev, role: e.target.value }))}
            className="form-input"
          >
            <option value="facts_admin">Portal Admin</option>
            <option value="university_staff">University Staff</option>
          </select>
        </div>
        {userForm.role === 'university_staff' && (
          <div className="form-group">
            <label className="form-label">University</label>
            <select
              value={userForm.university_id}
              onChange={(e) => setUserForm((prev) => ({ ...prev, university_id: e.target.value }))}
              className="form-input"
            >
              <option value="">Select University</option>
              {availableUniversities.map((uni) => (
                <option key={uni.id} value={uni.id}>{uni.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input
            type="tel"
            value={userForm.phone}
            onChange={(e) => setUserForm((prev) => ({ ...prev, phone: e.target.value }))}
            className="form-input"
          />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveUser} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
